import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, View } from 'react-native';
import { TWCAccessibleContent, TWCButton, TWCText } from '@components';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import { assets } from '@assets';
import { onboardingTestIDs, Theme } from '@constants';
import { useContextNodeHandle } from '@hooks';
import { navigationService } from '@services';
import { onboardingFlowState, onboardingLandingState, screenReaderEnabledState } from '@store';

import { styles } from './styles';

import { canPurchaseOnPlatform, isAndroid } from '../../../helpers/Platform';
import { accessibleDollarAmount } from '../../../helpers/StringUtils';
import { OnboardingBackgroundImage } from '../../onboarding/components/OnboardingBackgroundImage';

export const OnboardingHomePageContent: FC = () => {
  // We pre-fetch this feed on startup, so no need to handle loading state
  const { data } = useRecoilValue(onboardingLandingState);
  const screenReaderEnabled = useRecoilValue(screenReaderEnabledState);
  const resetOnboardingFlow = useResetRecoilState(onboardingFlowState);
  const { t } = useTranslation();

  const onSignInPress = () => {
    resetOnboardingFlow();
    navigationService.navigate(onboardingTestIDs.pages.subscriberSignInPage);
  };
  const onSignUpPress = () => {
    resetOnboardingFlow();
    // For native, start native sign up flow
    if (canPurchaseOnPlatform) {
      navigationService.navigate(onboardingTestIDs.pages.subscriberSignUpPage);
    } else {
      // For web do online trial page
      navigationService.navigate(onboardingTestIDs.pages.onboardingFreeTrialPage);
    }
  };

  const sectionHandle = useContextNodeHandle(onboardingTestIDs.onboardingHome.pageInfoSection);
  const subscribeHandle = useContextNodeHandle(onboardingTestIDs.onboardingHome.subscribeButton);
  const signInHandle = useContextNodeHandle(onboardingTestIDs.onboardingHome.signInButton);

  return (
    <OnboardingBackgroundImage
      testID={onboardingTestIDs.onboardingHome.pageBackgroundImage}
      uri={data?.backgroundImage}
    >
      <View style={styles.pageContent}>
        <TWCAccessibleContent
          disabledScreenreaderFocus={subscribeHandle}
          nextFocusDown={subscribeHandle}
          nextFocusLeft={undefined}
          nextFocusRight={undefined}
          nextFocusUp={undefined}
          testID={onboardingTestIDs.onboardingHome.pageInfoSection}
        >
          <>
            <Image
              accessibilityHint={''}
              accessibilityLabel={t('twc.logoAccessibilityLabel')}
              source={assets.twcLogo}
              style={styles.logo}
            />
            <TWCText
              importantForAccessibility={'yes'}
              numberOfLines={1}
              style={styles.pageTitle}
              testID={onboardingTestIDs.onboardingHome.pageTitle}
              variant={'boldText54'}
            >
              {data?.Title?.headline}
            </TWCText>
            <TWCText
              importantForAccessibility={'yes'}
              numberOfLines={3}
              style={styles.pageSubtitle}
              testID={onboardingTestIDs.onboardingHome.pageSubtitle}
              variant={'regularText36Transparent'}
            >
              {data?.Title?.body}
            </TWCText>
          </>
        </TWCAccessibleContent>
        <TWCText
          importantForAccessibility={'no'}
          numberOfLines={2}
          style={styles.describeText}
          testID={onboardingTestIDs.onboardingHome.subscribeText}
          variant={'regularText24'}
        >
          {data?.SubscribeCTA?.headline}
        </TWCText>
        <TWCButton
          accessibilityHint={accessibleDollarAmount(data?.SubscribeCTA?.headline)}
          accessibilityLabel={data?.SubscribeCTA?.call_to_action}
          nextFocusDown={signInHandle}
          nextFocusUp={screenReaderEnabled ? sectionHandle : isAndroid ? subscribeHandle : null}
          style={styles.topButton}
          styleFocused={{ backgroundColor: Theme.red }}
          testID={onboardingTestIDs.onboardingHome.subscribeButton}
          textFocusStyle={{ color: Theme.white }}
          title={data?.SubscribeCTA?.call_to_action}
          onPress={onSignUpPress}
        />
        <TWCText
          importantForAccessibility={'no'}
          numberOfLines={2}
          style={styles.describeText}
          testID={onboardingTestIDs.onboardingHome.signInText}
          variant={'regularText24'}
        >
          {data?.SignInCTA?.headline}
        </TWCText>
        <TWCButton
          accessibilityHint={data?.SignInCTA?.headline}
          accessibilityLabel={data?.SignInCTA?.call_to_action}
          nextFocusDown={null}
          nextFocusUp={subscribeHandle}
          testID={onboardingTestIDs.onboardingHome.signInButton}
          title={data?.SignInCTA?.call_to_action}
          width={230}
          onPress={onSignInPress}
        />
      </View>
    </OnboardingBackgroundImage>
  );
};
